import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePaperClip,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const SopDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    docType: '',
    url: '',
    version: '',
    validFrom: '',
    validTo: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadDocuments();
    } else {
      setError('Please login to view SOP documents');
      setLoading(false);
    }
  }, [tenantId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getSopDocuments(tenantId);
      setDocuments(data || []);
    } catch (err) {
      setError('Failed to load SOP documents. Please try again.');
      console.error('Error loading SOP documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDocuments = documents.filter((doc) =>
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.docType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingDoc(null);
    setFormData({
      title: '',
      docType: '',
      url: '',
      version: '',
      validFrom: '',
      validTo: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };
    setFormData({
      title: doc.title || '',
      docType: doc.docType || '',
      url: doc.url || '',
      version: doc.version || '',
      validFrom: formatDate(doc.validFrom),
      validTo: formatDate(doc.validTo),
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for SOP documents.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete SOP document. Please try again.');
      console.error('Error deleting SOP document:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const docData = {
        tenantId: tenantId,
        title: formData.title.trim(),
        docType: formData.docType.trim() || null,
        url: formData.url.trim() || null,
        version: formData.version.trim() || null,
        validFrom: formData.validFrom ? new Date(formData.validFrom) : null,
        validTo: formData.validTo ? new Date(formData.validTo) : null,
      };

      if (editingDoc) {
        docData.docId = editingDoc.docId;
        await masterDataService.updateSopDocument(editingDoc.docId, docData);
      } else {
        await masterDataService.createSopDocument(docData);
      }

      await loadDocuments();
      setShowModal(false);
      setFormData({ title: '', docType: '', url: '', version: '', validFrom: '', validTo: '' });
      setEditingDoc(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save SOP document. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDoc(null);
    setFormData({ title: '', docType: '', url: '', version: '', validFrom: '', validTo: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading SOP documents..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>SOP Documents</h1>
          <p>Manage SOP documents for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Document
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredDocuments.length} {filteredDocuments.length === 1 ? 'document' : 'documents'}
          </div>
        </div>

        {filteredDocuments.length === 0 ? (
          <div className="empty-state">
            <HiOutlinePaperClip className="empty-icon" />
            <h3>No documents found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first SOP document'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Document
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Type</th>
                <th>Version</th>
                <th>Valid From</th>
                <th>Valid To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => (
                <tr key={doc.docId}>
                  <td>{doc.docId}</td>
                  <td className="name-cell">{doc.title}</td>
                  <td>{doc.docType || '-'}</td>
                  <td>{doc.version || '-'}</td>
                  <td>{doc.validFrom ? new Date(doc.validFrom).toLocaleDateString() : '-'}</td>
                  <td>{doc.validTo ? new Date(doc.validTo).toLocaleDateString() : '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(doc)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingDoc ? 'Edit SOP Document' : 'Add New SOP Document'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sop-document-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingDoc
                  ? 'Updating...'
                  : 'Creating...'
                : editingDoc
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="sop-document-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="title">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={formErrors.title ? 'error' : ''}
                placeholder="Enter document title"
              />
              {formErrors.title && (
                <span className="field-error">{formErrors.title}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="docType">Doc Type</label>
              <input
                type="text"
                id="docType"
                value={formData.docType}
                onChange={(e) =>
                  setFormData({ ...formData, docType: e.target.value })
                }
                placeholder="Enter document type (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">URL / Path</label>
              <input
                type="text"
                id="url"
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="Enter URL or path (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="version">Version</label>
              <input
                type="text"
                id="version"
                value={formData.version}
                onChange={(e) =>
                  setFormData({ ...formData, version: e.target.value })
                }
                placeholder="Enter version (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="validFrom">Valid From</label>
              <input
                type="date"
                id="validFrom"
                value={formData.validFrom}
                onChange={(e) =>
                  setFormData({ ...formData, validFrom: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="validTo">Valid To</label>
              <input
                type="date"
                id="validTo"
                value={formData.validTo}
                onChange={(e) =>
                  setFormData({ ...formData, validTo: e.target.value })
                }
              />
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default SopDocumentsPage;
